import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { getArticleBySlug, conhecimentoArticles } from "@/data/conhecimentoData";
import Footer from "@/components/landing/Footer";

function renderParagraphWithLinks(text, links) {
  if (!links || Object.keys(links).length === 0) return text;

  const matches = [];
  for (const [phrase, path] of Object.entries(links)) {
    const index = text.indexOf(phrase);
    if (index !== -1) {
      matches.push({ phrase, path, index });
    }
  }

  if (matches.length === 0) return text;
  matches.sort((a, b) => a.index - b.index);

  const parts = [];
  let lastIndex = 0;

  for (const match of matches) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <Link
        key={match.index}
        to={match.path}
        className="text-[#F59E0B] hover:text-[#FBBF24] underline underline-offset-4 transition-colors"
      >
        {match.phrase}
      </Link>
    );
    lastIndex = match.index + match.phrase.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

export default function ConhecimentoArticlePage() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    if (!article) return;

    document.title = article.metaTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", article.metaDescription);

    window.scrollTo(0, 0);

    const articleLd = document.createElement("script");
    articleLd.type = "application/ld+json";
    articleLd.id = "article-ld";
    articleLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": article.schemaType || "Article",
      "headline": article.titleDisplay,
      "description": article.metaDescription,
      "url": `https://www.kpaction.com.br/conhecimento/${article.slug}`,
      "publisher": {
        "@type": "Organization",
        "@id": "https://www.kpaction.com.br/#organization",
        "name": "KpAction",
      },
    });

    const breadcrumbLd = document.createElement("script");
    breadcrumbLd.type = "application/ld+json";
    breadcrumbLd.id = "breadcrumb-ld";
    breadcrumbLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.kpaction.com.br",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Conhecimento",
          "item": "https://www.kpaction.com.br/conhecimento",
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": article.titleDisplay,
          "item": `https://www.kpaction.com.br/conhecimento/${article.slug}`,
        },
      ],
    });

    document.head.appendChild(articleLd);
    document.head.appendChild(breadcrumbLd);

    return () => {
      const a = document.getElementById("article-ld");
      const b = document.getElementById("breadcrumb-ld");
      if (a) document.head.removeChild(a);
      if (b) document.head.removeChild(b);
    };
  }, [article]);

  if (!article) return <Navigate to="/conhecimento" replace />;

  const currentIndex = conhecimentoArticles.findIndex((a) => a.slug === slug);
  const prevArticle = currentIndex > 0 ? conhecimentoArticles[currentIndex - 1] : null;
  const nextArticle =
    currentIndex < conhecimentoArticles.length - 1
      ? conhecimentoArticles[currentIndex + 1]
      : null;

  return (
    <div className="min-h-screen bg-[#0F1115]" data-testid="conhecimento-article-page">
      {/* Header */}
      <header className="border-b border-[#2A2F38] bg-[#0F1115]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link
            to="/conhecimento"
            className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#E5E7EB] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Voltar para Conhecimento</span>
          </Link>
          <a href="/" className="font-heading text-xl font-bold">
            <span className="text-[#F59E0B]">Kp</span>
            <span className="text-[#E5E7EB]">Action</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-12" aria-label="Breadcrumb" data-testid="breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-[#6B7280] flex-wrap">
              <li>
                <a href="/" className="hover:text-[#F59E0B] transition-colors">
                  Home
                </a>
              </li>
              <li><span className="text-[#2A2F38]">/</span></li>
              <li>
                <Link to="/conhecimento" className="hover:text-[#F59E0B] transition-colors">
                  Conhecimento
                </Link>
              </li>
              <li><span className="text-[#2A2F38]">/</span></li>
              <li className="text-[#9CA3AF]">{article.titleDisplay}</li>
            </ol>
          </nav>

          {/* Article Header */}
          <div className="mb-12">
            <span className="inline-block px-4 py-1 text-xs font-mono tracking-widest uppercase text-[#F59E0B] bg-[#0E3A46] border border-[#F59E0B]/20 rounded-sm mb-6">
              Conhecimento
            </span>

            <h1
              className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-[#E5E7EB] mb-4"
              data-testid="article-title"
            >
              {article.titleDisplay}
            </h1>

            <p className="text-base md:text-lg text-[#9CA3AF] leading-relaxed">
              {article.summary}
            </p>
          </div>

          {/* Article Body */}
          <article className="space-y-12" data-testid="article-body">
            {article.sections.map((section, sIdx) => (
              <section key={sIdx}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#E5E7EB] mb-6">
                  {section.heading}
                </h2>

                {section.items && (
                  <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6 mb-6">
                    <ul className="space-y-3">
                      {section.items.map((item, iIdx) => (
                        <li key={iIdx} className="flex items-start gap-3">
                          <span className="text-[#F59E0B] mt-1 flex-shrink-0">
                            &bull;
                          </span>
                          <span className="text-[#E5E7EB]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-4">
                  {section.paragraphs.map((p, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-[#9CA3AF] text-base md:text-lg leading-relaxed"
                    >
                      {article.links
                        ? renderParagraphWithLinks(p, article.links)
                        : p}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </article>

          {/* Divider */}
          <div className="h-px bg-[#2A2F38] my-16" />

          {/* Related Articles Navigation */}
          <div className="space-y-4">
            <h3 className="font-heading text-xl font-bold text-[#E5E7EB] mb-6">
              Outros artigos
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {prevArticle && (
                <Link
                  to={`/conhecimento/${prevArticle.slug}`}
                  className="flex items-center gap-3 p-4 bg-[#1C1F26] border border-[#2A2F38] rounded-sm hover:border-[#F59E0B]/50 transition-all group"
                  data-testid="prev-article-link"
                >
                  <ArrowLeft className="h-4 w-4 text-[#F59E0B] flex-shrink-0 group-hover:-translate-x-1 transition-transform" />
                  <span className="text-sm text-[#9CA3AF] group-hover:text-[#E5E7EB] transition-colors">
                    {prevArticle.titleDisplay}
                  </span>
                </Link>
              )}
              {nextArticle && (
                <Link
                  to={`/conhecimento/${nextArticle.slug}`}
                  className="flex items-center justify-end gap-3 p-4 bg-[#1C1F26] border border-[#2A2F38] rounded-sm hover:border-[#F59E0B]/50 transition-all group md:col-start-2"
                  data-testid="next-article-link"
                >
                  <span className="text-sm text-[#9CA3AF] group-hover:text-[#E5E7EB] transition-colors text-right">
                    {nextArticle.titleDisplay}
                  </span>
                  <ArrowRight className="h-4 w-4 text-[#F59E0B] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
            </div>

            {/* Back to Hub */}
            <div className="text-center pt-8">
              <Link
                to="/conhecimento"
                className="inline-flex items-center gap-2 text-sm text-[#F59E0B] hover:text-[#FBBF24] transition-colors"
                data-testid="back-to-hub-link"
              >
                <BookOpen className="h-4 w-4" />
                Ver todos os artigos
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
